import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useAccount } from 'wagmi';

import Ellipse from 'src/components/Ellipse';
import NotSupportMobile from 'src/components/NotSupportMobile';
import SwitchNetworkModal, { type SwitchNetworkHandle } from 'src/components/SwitchNetworkModal';
import { colors } from 'src/global/styles/colors';
import { useWeb3 } from 'src/hooks/useWeb3';
import { useNetwork } from 'src/provider/NetworkProvider';

import Breadcrumbs from './Breadcumb';
import Footer from './Footer';
import Header from './Header';

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { address, status } = useAccount();
  const prevStatus = useRef(status);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isWrongNetwork } = useWeb3();
  const switchNetworkModalRef = useRef<SwitchNetworkHandle>(null);
  const { selectedNetwork, isNetworkInitialized } = useNetwork();

  // Wait a short time after initialization before checking authentication
  // This avoids race conditions with wallet connection status
  const [isReady, setIsReady] = useState(false);

  const showSwitchNetworkModal = useCallback(() => {
    switchNetworkModalRef.current?.show();
  }, [switchNetworkModalRef.current]);

  const hideSwitchNetworkModal = useCallback(() => {
    switchNetworkModalRef.current?.hide();
  }, [switchNetworkModalRef.current]);

  useEffect(() => {
    if (isNetworkInitialized) {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isNetworkInitialized]);

  useEffect(() => {
    if (!isReady) return;

    // Current status checks
    const isProtectedRoute = pathname !== '/';

    if (status === 'disconnected') {
      if (prevStatus.current === 'connected' || isProtectedRoute) {
        navigate('/core-sale');
      }
    }

    // Cleanup function will run before next effect execution
    return () => {
      prevStatus.current = status;
    };
  }, [status, selectedNetwork, isReady, pathname]);

  useEffect(() => {
    if (!isReady || !isNetworkInitialized) return;
    if (!address) {
      hideSwitchNetworkModal();
    } else if (isWrongNetwork) {
      showSwitchNetworkModal();
    } else {
      hideSwitchNetworkModal();
    }
  }, [address, isReady, isNetworkInitialized, isWrongNetwork]);

  return (
    <Stack position={'relative'} paddingTop={6} flexGrow={1} flex={1} height={'100%'} minHeight={'100dvh'}>
      <SwitchNetworkModal ref={switchNetworkModalRef} />
      <Ellipse
        width={777}
        height={561}
        color={colors.red300}
        blur={225}
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: 300,
            height: 200,
          },
          position: 'absolute',
          top: 0,
          right: 0,
          opacity: 0.15,
        }}
      />
      <Ellipse
        width={1009}
        height={647}
        color={colors.brand300}
        blur={225}
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: 300,
            height: 200,
          },
          [theme.breakpoints.down('lg')]: {
            width: 600,
            height: 400,
          },
          position: 'absolute',
          top: 300,
          right: 100,
          opacity: 0.15,
        }}
      />
      <Ellipse
        width={386}
        height={386}
        color={colors.red300}
        blur={225}
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: 100,
            height: 100,
          },
          position: 'absolute',
          top: 300,
          left: 0,
          opacity: 0.15,
        }}
      />
      <Header />
      {isMobile ? (
        <NotSupportMobile />
      ) : (
        <Stack
          flex={1}
          flexGrow={1}
          paddingY={5}
          zIndex={1300}
          width={'100%'}
          border={`1px solid ${colors.neutral015}`}
          borderLeft={'none'}
          borderRight={'none'}
          alignItems={'center'}
          justifyContent={'center'}>
          <Stack height={'100%'} flexGrow={1} width={'min(1344px, 100%)'} paddingX={{ xl: 0, xs: 2 }}>
            <Stack flexGrow={1} width={'100%'} height={'100%'}>
              <Stack direction={'row'} justifyContent={'start'} width={'100%'} maxWidth={'728px'}>
                <Breadcrumbs />
              </Stack>
              <Outlet />
            </Stack>
          </Stack>
        </Stack>
      )}
      <Footer />
    </Stack>
  );
};

export default AdminLayout;
